import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Plus, 
  Loader2, 
  RefreshCw,
  Users,
  UserCheck,
  TrendingUp,
  Target,
  LayoutList,
  Columns,
  Mail,
  Phone,
  User,
  Clock,
  MoreVertical,
  Calendar
} from 'lucide-react';
import { 
  useLeads, 
  useMoveLead,
  useCreateLead 
} from '../../hooks/useCRM';
import { useUsers } from '../../hooks/useUsers';
import PipelineColumn from '../../components/crm/PipelineColumn';
import QuickLeadModal from '../../components/crm/QuickLeadModal';
import LeadFilters from '../../components/crm/LeadFilters';

// Pipeline stages configuration (from API DEFAULT_PIPELINE_STAGES)
const PIPELINE_STAGES = [
  { name: 'new', label: 'New Lead', color: 'gray', description: 'Newly created leads' },
  { name: 'contacted', label: 'Contacted', color: 'blue', description: 'Initial contact made' },
  { name: 'tour_scheduled', label: 'Tour Scheduled', color: 'purple', description: 'School tour scheduled' },
  { name: 'tour_completed', label: 'Tour Completed', color: 'yellow', description: 'Tour completed' },
  { name: 'enrollment_pending', label: 'Enrollment Pending', color: 'orange', description: 'Enrollment in progress' },
  { name: 'enrolled', label: 'Enrolled', color: 'green', description: 'Successfully enrolled' },
  { name: 'lost', label: 'Lost', color: 'red', description: 'Lead lost' }
];

/**
 * CRMPipeline - Main Kanban board for managing leads
 */
const CRMPipeline = () => {
  const navigate = useNavigate();
  const { tenantId } = useParams();
  
  // State for filters and search
  const [searchQuery, setSearchQuery] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [assignedFilter, setAssignedFilter] = useState('');
  const [dateRangeFilter, setDateRangeFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Modal state
  const [quickAddModal, setQuickAddModal] = useState({
    isOpen: false,
    stage: null
  });
  
  // View mode state (kanban/list toggle)
  const [viewMode, setViewMode] = useState('kanban'); // 'kanban' | 'list'
  
  // Auto-detect mobile and switch to list view
  useEffect(() => {
    const checkMobile = () => {
      const isMobile = window.innerWidth < 1024; // lg breakpoint
      if (isMobile && viewMode === 'kanban') {
        setViewMode('list');
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [viewMode]);

  // Fetch data
  const { 
    data: leadsData, 
    isLoading: leadsLoading, 
    refetch 
  } = useLeads({
    stage: stageFilter || undefined,
    source: sourceFilter || undefined,
    assigned_to: assignedFilter === 'unassigned' ? 'none' : assignedFilter || undefined,
    search: searchQuery || undefined,
    date_range: dateRangeFilter || undefined
  });
  
  const { data: usersData } = useUsers();
  
  // Mutations
  const moveLeadMutation = useMoveLead();
  const createLeadMutation = useCreateLead();

  // Extract data - wrapped in useMemo to avoid recreating references on every render
  const allLeads = useMemo(() => leadsData?.leads || [], [leadsData?.leads]);
  const staff = useMemo(() => usersData?.users?.filter(u => 
    u.role === 'admin' || u.role === 'super_admin' || u.role === 'employee'
  ) || [], [usersData?.users]);

  // Group leads by stage
  const leadsByStage = useMemo(() => {
    const grouped = {};
    
    // Initialize all stages with empty arrays
    PIPELINE_STAGES.forEach(stage => {
      grouped[stage.name] = [];
    });
    
    // Group leads
    allLeads.forEach(lead => {
      const stageName = lead.stage;
      if (grouped[stageName]) {
        grouped[stageName].push(lead);
      }
    });
    
    return grouped;
  }, [allLeads]);

  // Stats
  const stats = useMemo(() => {
    const total = allLeads.length;
    
    // New this week
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    const newThisWeek = allLeads.filter(lead => {
      const createdDate = new Date(lead.created_at);
      return createdDate >= oneWeekAgo;
    }).length;
    
    // Enrolled count
    const enrolled = allLeads.filter(lead => lead.stage === 'enrolled').length;
    
    // Conversion rate (enrolled / total active leads * 100)
    const activeLeads = allLeads.filter(lead => lead.stage !== 'lost');
    const conversionRate = activeLeads.length > 0 
      ? Math.round((enrolled / activeLeads.length) * 100) 
      : 0;
    
    return { total, newThisWeek, enrolled, conversionRate };
  }, [allLeads]);

  // Handlers
  const handleMoveStage = async (leadId, newStage, reason = '') => {
    try {
      await moveLeadMutation.mutateAsync({ 
        id: leadId, 
        stage: newStage
      });
    } catch (error) {
      console.error('Failed to move lead:', error);
      alert('Failed to move lead: ' + error.message);
    }
  };

  const handleQuickAdd = (stage) => {
    setQuickAddModal({
      isOpen: true,
      stage
    });
  };

  const handleQuickAddSubmit = async (formData) => {
    try {
      const data = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        source: formData.source || 'other',
        assignedTo: formData.assignedTo || null,
        interestedClasses: formData.interestedClasses,
        notes: formData.notes,
        stage: quickAddModal.stage?.name || 'new'
      };
      
      await createLeadMutation.mutateAsync(data);
      setQuickAddModal({ isOpen: false, stage: null });
    } catch (error) {
      console.error('Failed to create lead:', error);
      alert('Failed to create lead: ' + error.message);
    }
  };

  const handleScheduleTour = (lead) => {
    // Navigate to lead detail with tour scheduling open
    navigate(`/${tenantId}/dashboard/crm/leads/${lead.id}?tab=tour`);
  };

  const handleSendEmail = (lead) => {
    const subject = `Re: Your inquiry about enrollment`;
    const mailtoLink = `mailto:${lead.email}?subject=${encodeURIComponent(subject)}`;
    window.open(mailtoLink, '_blank');
  };

  const handleViewDetails = (lead) => {
    navigate(`/${tenantId}/dashboard/crm/leads/${lead.id}`);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStageFilter('');
    setSourceFilter('');
    setAssignedFilter('');
    setDateRangeFilter('');
  };

  // eslint-disable-next-line no-unused-vars
  const _hasActiveFilters = searchQuery || stageFilter || sourceFilter || assignedFilter || dateRangeFilter;

  // Loading state
  if (leadsLoading && !allLeads.length) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-purple-600" size={32} />
      </div>
    );
  }

  return (
    <div className="animate-fade-in h-full flex flex-col">
      {/* Header Section */}
      <div className="mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">CRM Pipeline</h1>
            <p className="text-gray-500 mt-1">Manage leads and track enrollment funnel.</p>
          </div>
          
          {/* Stats Cards */}
          <div className="flex flex-wrap gap-3">
            <div className="bg-white px-4 py-2 rounded-lg shadow-sm border border-purple-100 flex items-center space-x-2">
              <Users size={18} className="text-purple-600" />
              <div>
                <p className="text-xs text-gray-500">Total Leads</p>
                <p className="font-semibold text-gray-900">{stats.total}</p>
              </div>
            </div>
            <div className="bg-white px-4 py-2 rounded-lg shadow-sm border border-blue-100 flex items-center space-x-2">
              <Target size={18} className="text-blue-600" />
              <div>
                <p className="text-xs text-gray-500">New This Week</p>
                <p className="font-semibold text-blue-700">{stats.newThisWeek}</p>
              </div>
            </div>
            <div className="bg-white px-4 py-2 rounded-lg shadow-sm border border-green-100 flex items-center space-x-2">
              <UserCheck size={18} className="text-green-600" />
              <div>
                <p className="text-xs text-gray-500">Enrolled</p>
                <p className="font-semibold text-green-700">{stats.enrolled}</p>
              </div>
            </div>
            <div className="bg-white px-4 py-2 rounded-lg shadow-sm border border-orange-100 flex items-center space-x-2">
              <TrendingUp size={18} className="text-orange-600" />
              <div>
                <p className="text-xs text-gray-500">Conversion Rate</p>
                <p className="font-semibold text-orange-700">{stats.conversionRate}%</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Component */}
      <LeadFilters
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        stageFilter={stageFilter}
        setStageFilter={setStageFilter}
        sourceFilter={sourceFilter}
        setSourceFilter={setSourceFilter}
        assignedFilter={assignedFilter}
        setAssignedFilter={setAssignedFilter}
        dateRangeFilter={dateRangeFilter}
        setDateRangeFilter={setDateRangeFilter}
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        staff={staff}
        onClearFilters={clearFilters}
      />

      {/* Action Bar */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => refetch()}
            className="flex items-center space-x-2 px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors text-sm"
            disabled={leadsLoading}
          >
            <RefreshCw size={16} className={leadsLoading ? 'animate-spin' : ''} />
            <span>Refresh</span>
          </button>
          
          {/* View Mode Toggle */}
          <button
            onClick={() => setViewMode(viewMode === 'kanban' ? 'list' : 'kanban')}
            className="flex items-center space-x-2 px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors text-sm lg:hidden"
            title={viewMode === 'kanban' ? 'Switch to list view' : 'Switch to kanban view'}
          >
            {viewMode === 'kanban' ? <LayoutList size={16} /> : <Columns size={16} />}
            <span>{viewMode === 'kanban' ? 'List' : 'Kanban'}</span>
          </button>
        </div>

        <button
          onClick={() => handleQuickAdd(PIPELINE_STAGES[0])}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus size={20} />
          <span>Add Lead</span>
        </button>
      </div>

      {/* Kanban Board or List View */}
      {viewMode === 'kanban' ? (
        <div className="flex-1 overflow-x-auto overflow-y-hidden">
          <div className="flex space-x-4 pb-4 min-w-max">
            {PIPELINE_STAGES.map(stage => (
              <PipelineColumn
                key={stage.name}
                stage={stage}
                leads={leadsByStage[stage.name] || []}
                allStages={PIPELINE_STAGES}
                onMoveStage={handleMoveStage}
                onScheduleTour={handleScheduleTour}
                onSendEmail={handleSendEmail}
                onViewDetails={handleViewDetails}
                onQuickAdd={handleQuickAdd}
                isLoading={leadsLoading && !leadsByStage[stage.name]?.length}
              />
            ))}
          </div>
        </div>
      ) : (
        /* Mobile List View */
        <div className="flex-1 overflow-y-auto pb-4">
          <div className="space-y-4">
            {PIPELINE_STAGES.map(stage => {
              const stageLeads = leadsByStage[stage.name] || [];
              if (stageLeads.length === 0) return null;
              
              return (
                <div key={stage.name} className="bg-white rounded-lg shadow-sm border">
                  <div 
                    className="px-4 py-3 border-b flex items-center justify-between"
                    style={{ borderLeftColor: stage.color, borderLeftWidth: '4px' }}
                  >
                    <div>
                      <h3 className="font-medium text-gray-900">{stage.label}</h3>
                      <span className="text-sm text-gray-500">{stageLeads.length} leads</span>
                    </div>
                    <div 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: stage.color }}
                    />
                  </div>
                  <div className="divide-y divide-gray-100">
                    {stageLeads.map(lead => (
                      <LeadListItem 
                        key={lead.id} 
                        lead={lead} 
                        stage={stage}
                        onViewDetails={() => handleViewDetails(lead)}
                        onScheduleTour={() => handleScheduleTour(lead)}
                        onSendEmail={() => handleSendEmail(lead)}
                        onMoveStage={handleMoveStage}
                        allStages={PIPELINE_STAGES}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Quick Add Modal */}
      <QuickLeadModal
        isOpen={quickAddModal.isOpen}
        onClose={() => setQuickAddModal({ isOpen: false, stage: null })}
        onSubmit={handleQuickAddSubmit}
        initialStage={quickAddModal.stage}
        staff={staff}
        isLoading={createLeadMutation.isPending}
      />
    </div>
  );
};

/**
 * LeadListItem - Compact list item for mobile view
 */
const LeadListItem = ({ 
  lead, 
  stage,
  onViewDetails,
  onScheduleTour,
  onSendEmail,
  onMoveStage,
  allStages
}) => {
  const [showActions, setShowActions] = useState(false);

  // Calculate days since created
  const getDaysSince = (dateString) => {
    if (!dateString) return 0;
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysSince = getDaysSince(lead.created_at);

  // Get initials for avatar
  const getInitials = () => {
    const first = lead.first_name?.[0] || '';
    const last = lead.last_name?.[0] || '';
    return (first + last).toUpperCase();
  };

  const assignedStaff = lead.assigned_to_user;
  const currentStageIndex = allStages.findIndex(s => s.name === lead.stage);
  const canMoveLeft = currentStageIndex > 0;
  const canMoveRight = currentStageIndex < allStages.length - 1 && 
                       lead.stage !== 'enrolled' && 
                       lead.stage !== 'lost';

  return (
    <div className="p-4 hover:bg-gray-50 transition-colors">
      <div className="flex items-start justify-between">
        <div 
          className="flex items-center space-x-3 flex-1 cursor-pointer"
          onClick={onViewDetails}
        >
          <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-purple-700 font-semibold text-sm">
              {getInitials()}
            </span>
          </div>
          <div className="min-w-0 flex-1">
            <h4 className="font-medium text-gray-900 text-sm truncate">
              {lead.first_name} {lead.last_name}
            </h4>
            <div className="flex items-center text-xs text-gray-500 mt-0.5 space-x-2">
              <span className="flex items-center">
                <Mail size={10} className="mr-1" />
                {lead.email || 'No email'}
              </span>
              {lead.phone && (
                <span className="flex items-center">
                  <Phone size={10} className="mr-1" />
                  {lead.phone}
                </span>
              )}
            </div>
            <div className="flex items-center text-xs text-gray-500 mt-1 space-x-3">
              <span className="flex items-center">
                <Clock size={10} className="mr-1" />
                {daysSince === 0 ? 'Today' : daysSince === 1 ? '1 day ago' : `${daysSince} days ago`}
              </span>
              {assignedStaff ? (
                <span className="flex items-center">
                  <User size={10} className="mr-1" />
                  {assignedStaff.first_name} {assignedStaff.last_name?.[0]}.
                </span>
              ) : (
                <span className="text-gray-400 italic">Unassigned</span>
              )}
            </div>
          </div>
        </div>
        
        {/* Quick Actions */}
        <div className="relative ml-2">
          <button 
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
            onClick={(e) => {
              e.stopPropagation();
              setShowActions(!showActions);
            }}
          >
            <MoreVertical size={16} />
          </button>
          
          {showActions && (
            <>
              <div 
                className="fixed inset-0 z-10"
                onClick={() => setShowActions(false)}
              />
              <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                <button
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-purple-50 flex items-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewDetails();
                    setShowActions(false);
                  }}
                >
                  <User size={14} className="mr-2" />
                  View Details
                </button>
                <button
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-purple-50 flex items-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    onScheduleTour();
                    setShowActions(false);
                  }}
                >
                  <Calendar size={14} className="mr-2" />
                  Schedule Tour
                </button>
                <button
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-purple-50 flex items-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSendEmail();
                    setShowActions(false);
                  }}
                >
                  <Mail size={14} className="mr-2" />
                  Send Email
                </button>
                <hr className="my-1" />
                {canMoveLeft && (
                  <button
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-purple-50 flex items-center"
                    onClick={(e) => {
                      e.stopPropagation();
                      onMoveStage(lead.id, allStages[currentStageIndex - 1].name);
                      setShowActions(false);
                    }}
                  >
                    ← Move to {allStages[currentStageIndex - 1]?.label}
                  </button>
                )}
                {canMoveRight && (
                  <button
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-purple-50 flex items-center"
                    onClick={(e) => {
                      e.stopPropagation();
                      onMoveStage(lead.id, allStages[currentStageIndex + 1].name);
                      setShowActions(false);
                    }}
                  >
                    → Move to {allStages[currentStageIndex + 1]?.label}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CRMPipeline;
